import { Box, Button, Typography } from "@mui/material";
import { MainContainer } from "../../components/MainContainerComp";
import { ProgressBar } from "../../components/ProgressBar";
import { ITheme, theme } from "../../utils/theme";
import { VerifyEmail } from "./components/VerifyEmail";
import { UpdateAddress } from "./components/UpdateAddress";
import { Confirmation } from "./components/Confirmation";
import { IContactEmailData, IContactAddressData, IAddressPredictionsData, IAddressDetailsData } from "./ContactAddressData";
import { ProgressBarSteps } from "./ContactAddressModels";
import { useFetcher } from "react-router-dom";

interface IContactAddressProps {
  data: IContactEmailData;
  submitting: boolean;
  activeStep: string;
  onChangeField: (field: keyof IContactAddressData, value: string) => void;
  email: string;
  onSubmit: () => void;
  error: boolean;
  setError: React.Dispatch<React.SetStateAction<boolean>>;
  setAddressFormDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  onNext: () => void;
  isNextDisabled: boolean;
  isFormTouched: boolean;
  isAddressFormTouched: boolean;
  isAddressFormDisabled: boolean;
  submissionSuccess: boolean;
  validatedAddress: boolean;
  setValidatedAddress: React.Dispatch<React.SetStateAction<boolean>>;
  isAddressValid: () => boolean;
  isAddressSameAsStored: () => boolean;
  setLastValidatedAddress: React.Dispatch<React.SetStateAction<{
    addressLine1: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  } | null>>;
  hasVerifiedEmail: boolean;
  isValidEmail: (email: string) => boolean;
  onSetEmail: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onSubmitEmail: () => void;
  onClose: () => void;
  predictionsFetcher: ReturnType<typeof useFetcher<IAddressPredictionsData>>;
  detailsFetcher: ReturnType<typeof useFetcher<IAddressDetailsData>>;
}

export const ContactAddress: React.FC<IContactAddressProps> = ({
  data,
  submitting,
  activeStep,
  onChangeField,
  email,
  onSubmit,
  error,
  setError,
  setAddressFormDisabled,
  onNext,
  isFormTouched,
  isAddressFormTouched,
  isAddressFormDisabled,
  isValidEmail,
  onSetEmail,
  submissionSuccess,
  validatedAddress,
  setValidatedAddress,
  setLastValidatedAddress,
  isAddressValid,
  isAddressSameAsStored,
  hasVerifiedEmail,
  onSubmitEmail,
  onClose,
  predictionsFetcher,
  detailsFetcher,
}) => {
  const isEmailStep = activeStep === ProgressBarSteps.VerifyEmail;
  const isAddressStep = activeStep === ProgressBarSteps.UpdateAddress;
  const isConfirmationStep = activeStep === ProgressBarSteps.Confirmation;
  const readOnly = hasVerifiedEmail || submissionSuccess;
  const emailError =
    (!email && isFormTouched) || (!!email && !isValidEmail(email));
  return (
    <MainContainer hasGreyBg>
      <ProgressBar
        steps={Object.values(ProgressBarSteps)}
        activeStep={activeStep}
      />
      {isEmailStep && (
        <>
          <Typography
            component="div"
            sx={{
              fontWeight: "bold",
              color: theme.palette.corporateBlue,
              ml: 2,
              pt: 2,
              pb: 3,
            }}
          >
            This Update feature requires a verified email. Please provide an
            email address.
          </Typography>
          <VerifyEmail
            data={data}
            email={email}
            emailError={emailError}
            onSetEmail={onSetEmail}
            submissionSuccess={submissionSuccess}
            readOnly={readOnly}
          />
        </>
      )}
      {isAddressStep && (
        <UpdateAddress
          onChangeField={onChangeField}
          data={data}
          error={error}
          setError={setError}
          setAddressFormDisabled={setAddressFormDisabled}
          isAddressFormTouched={isAddressFormTouched}
          isAddressFormDisabled={isAddressFormDisabled}
          validatedAddress={validatedAddress}
          setLastValidatedAddress={setLastValidatedAddress}
          isAddressValid={isAddressValid}
          setValidatedAddress={setValidatedAddress}
          predictionsFetcher={predictionsFetcher}
          detailsFetcher={detailsFetcher}

        />
      )}
      {isConfirmationStep && <Confirmation />}
      <Box
        sx={{
          display: "flex",
          justifyContent: isEmailStep ? "space-between" : "center",
          mr: isEmailStep ? { md: "17%" } : 0,
          ml: isEmailStep ? { md: "17%" } : 0,
          px: 2,
        }}
      >
        {isEmailStep && (
          <Button
            disabled={submitting 
              || emailError || !isFormTouched || submissionSuccess || hasVerifiedEmail}
            size="large"
            variant="contained"
            onClick={onSubmitEmail}
            data-automation="update-btn"
          >
            Update
          </Button>
        )}
        {isEmailStep && (
          <Button
            disabled={!hasVerifiedEmail}
            size="large"
            variant="contained"
            onClick={onNext}
            data-automation="next-btn"
          >
            Next
          </Button>
        )}
        {isAddressStep && (
          <Button
            disabled={submitting || error || !isAddressFormTouched || !isAddressValid() || isAddressSameAsStored()}
            size="large"
            variant="contained"
            onClick={onSubmit}
            data-automation="submit-btn"
          >
            Submit
          </Button>
        )}
        {isConfirmationStep && (
          <Button
            size="large"
            variant="contained"
            onClick={onClose}
            data-automation="close-btn"
          >
            Close
          </Button>
        )}
      </Box>
    </MainContainer>
  );
};
